import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from './contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import env from './utils/env';
import './App.css';

interface ReportDetailData {
    _id: string;
    filename?: string;
    has_isin?: boolean;
    report_source_ref?: string;
    update_timestamp?: string;
    issuer?: string;
    listOfAllBondISINs?: string[];
    listOfAllBondVolumes?: string[];
    allCountriesOfBonds?: string[];
    listOfAllBondCurrencies?: string[];
    number_of_bonds?: number;
    projects?: any[];
    reasoning?: string;
    reportCreationDate?: string;
    reportType?: string;
    summary?: string;
    totalGreenLoanPortfolio?: string;
    totalImpact?: string;
    total_GHG_emissions_avoided?: string;
    yearOfReport?: string;
    validation_timestamp?: string;
    all_missing_bond_isins?: string[];
    number_of_missing_bonds?: number;
    search_result_ref?: string;
    validation_cusip?: string;
    validation_isin?: string;
    isin?: string;
    cusip?: string;
    has_cusip?: boolean;
    report_ref?: string[];
    title?: string;
    author?: string;
    tags?: string[];
    classification_results?: {
        plan?: string;
        category?: string[];
        class_probabilities?: number[];
        short_description?: string;
        reasoning?: string[];
    };
}

const ReportDetailView: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { token, authLoaded } = useAuth();
    const { t } = useTranslation();
    const [report, setReport] = useState<ReportDetailData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [showRawData, setShowRawData] = useState<boolean>(false);

    const apiKey = env.apiKey;
    const apiUrl = env.apiUrl;

    useEffect(() => {
        if (authLoaded && id) {
            fetchReportDetails();
        }
    }, [authLoaded, id]);

    const fetchReportDetails = async () => {
        if (!id) return;

        setLoading(true);
        setError(null);

        const headers: Record<string, string> = {
            'x-api-key': `${apiKey}`
        };
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        try {
            const response = await axios.get(`${apiUrl}/reports/${id}`, {
                headers
            });
            setReport(response.data);
        } catch (error) {
            console.error('Error fetching report details:', error);
            setError('Failed to load report details. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return '-';
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return dateString;
        }
    };

    const formatArray = (arr?: string[]) => {
        if (!arr || arr.length === 0) return '-';
        return arr.join(', ');
    };

    const handleBack = () => {
        const urlParams = new URLSearchParams(location.search);
        const returnTo = urlParams.get('returnTo');
        
        if (returnTo) {
            // Navigate back to the preserved state
            navigate(decodeURIComponent(returnTo));
        } else {
            // Default fallback
            navigate('/reports');
        }
    };

    if (loading) {
        return (
            <div className="container">
                <div className="loading-container">
                    <div className="bonds-loader-flex">
                        <i className="fas fa-spinner fa-spin loader-icon" aria-label="Loading" />
                        <span className="loader-text">
                            {t('loading.reports', 'Loading report details...')}
                        </span>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container">
                <div className="error-message">
                    <p>{error}</p>
                    <button className="retry-button" onClick={fetchReportDetails}>
                        {t('buttons.retry', 'Retry')}
                    </button>
                    <button className="secondary-button" onClick={handleBack} style={{ marginLeft: '10px' }}>
                        {t('buttons.back', 'Back')}
                    </button>
                </div>
            </div>
        );
    }

    if (!report) {
        return (
            <div className="container">
                <div className="error-message">
                    <p>Report not found.</p>
                    <button className="secondary-button" onClick={handleBack}>
                        {t('buttons.back', 'Back')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={{ width: '100%', maxWidth: '100%', padding: '0 20px', backgroundColor: 'white', boxSizing: 'border-box', overflowX: 'hidden' }}>
            <div className="detail-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <button onClick={handleBack} className="back-button">
                        <i className="fas fa-arrow-left"></i>
                        {t('buttons.back', 'Back to Reports')}
                    </button>
                    <h1 style={{ margin: 0, color: '#333' }}>{report.title || report.filename || 'Report Details'}</h1>
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                        className="btn-secondary-action"
                        onClick={() => setShowRawData(!showRawData)}
                    >
                        <i className="fas fa-code"></i>
                        {showRawData ? 'Hide Raw Data' : 'Show Raw Data'}
                    </button>
                </div>
            </div>

            <div style={{ width: '100%', backgroundColor: 'white', boxSizing: 'border-box' }}>
                {/* Basic Information */}
                <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '8px', border: '1px solid #e0e0e0', marginBottom: '20px', boxSizing: 'border-box' }}>
                    <h3 style={{ color: '#333', textAlign: 'left', marginBottom: '20px' }}>Basic Information</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(max(300px, 33.33%), 1fr))', maxWidth: '100%', gap: '16px', boxSizing: 'border-box' }}>
                        <div className="detail-item">
                            <div className="detail-label-small">ID</div>
                            <div className="detail-value-large" style={{ fontFamily: 'monospace', fontSize: '14px', wordBreak: 'break-all' }}>{report._id}</div>
                        </div>
                        <div className="detail-item">
                            <div className="detail-label-small">Issuer</div>
                            <div className="detail-value-large">{report.issuer || '-'}</div>
                        </div>
                        <div className="detail-item">
                            <div className="detail-label-small">Report Type</div>
                            <div className="detail-value-large">{report.reportType || '-'}</div>
                        </div>
                        <div className="detail-item">
                            <div className="detail-label-small">Creation Date</div>
                            <div className="detail-value-large">{formatDate(report.reportCreationDate)}</div>
                        </div>
                        <div className="detail-item">
                            <div className="detail-label-small">Year of Report</div>
                            <div className="detail-value-large">{report.yearOfReport || '-'}</div>
                        </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px', marginTop: '16px' }}>
                        <div className="detail-item">
                            <div className="detail-label-small">Title</div>
                            <div className="detail-value-large" style={{ wordBreak: 'break-word' }}>{report.title || '-'}</div>
                        </div>
                        <div className="detail-item">
                            <div className="detail-label-small">Author</div>
                            <div className="detail-value-large">{report.author || '-'}</div>
                        </div>
                    </div>
                </div>

              {/* Preview Document */}
              {report.report_source_ref && (
                <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '8px', border: '1px solid #e0e0e0', marginBottom: '20px', boxSizing: 'border-box' }}>
                  <h3 style={{ color: '#333', textAlign: 'left', marginBottom: '20px' }}>Filename</h3>
                  <div className="detail-item">
                    <div className="detail-value-large" style={{ fontFamily: 'monospace', fontSize: '14px', wordBreak: 'break-all' }}>{report.filename || '-'}</div>
                  </div>
                  <button
                    className="btn-secondary-action"
                    onClick={() => navigate(`/report-sources?doc=${report.report_source_ref}&returnTo=${encodeURIComponent(location.pathname)}`)}
                  >
                    <i className="fas fa-eye"></i>
                    Preview Document
                  </button>
                </div>
              )}

                {/* Filename */}
                {/*<div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '8px', border: '1px solid #e0e0e0', marginBottom: '20px', boxSizing: 'border-box' }}>*/}
                {/*    <h3 style={{ color: '#333', textAlign: 'left', marginBottom: '20px' }}>Filename</h3>*/}
                {/*    <div className="detail-item">*/}
                {/*        <div className="detail-value-large" style={{ fontFamily: 'monospace', fontSize: '14px', wordBreak: 'break-all' }}>{report.filename || '-'}</div>*/}
                {/*    </div>*/}
                {/*</div>*/}

                {/* Summary */}
                {report.summary && (
                    <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '8px', border: '1px solid #e0e0e0', marginBottom: '20px', boxSizing: 'border-box' }}>
                        <h3 style={{ color: '#333', textAlign: 'left', marginBottom: '20px' }}>Summary</h3>
                        <div className="detail-value-large" style={{ wordBreak: 'break-word', lineHeight: '1.6' }}>{report.summary}</div>
                    </div>
                )}

                {/* Classification Results */}
                {report.classification_results && (
                    <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '8px', border: '1px solid #e0e0e0', marginBottom: '20px', boxSizing: 'border-box' }}>
                        <h3 style={{ color: '#333', textAlign: 'left', marginBottom: '20px' }}>Classification Results</h3>

                        {report.classification_results.short_description && (
                            <div style={{ marginBottom: '20px' }}>
                                <div className="detail-label-small">Description</div>
                                <div className="detail-value-large" style={{ marginTop: '8px', lineHeight: '1.6' }}>
                                    {report.classification_results.short_description}
                                </div>
                            </div>
                        )}

                        {report.classification_results.category && report.classification_results.category.length > 0 && (
                            <div style={{ marginBottom: '20px' }}>
                                <div className="detail-label-small">Categories</div>
                                <div className="projects-list" style={{ marginTop: '12px' }}>
                                    <table className="projects-table">
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>Category</th>
                                                <th>Probability</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {report.classification_results.category.map((cat: string, index: number) => (
                                                <tr key={index}>
                                                    <td>{index + 1}</td>
                                                    <td>{cat}</td>
                                                    <td>
                                                        {report.classification_results?.class_probabilities?.[index]
                                                            ? `${(report.classification_results.class_probabilities[index] * 100).toFixed(1)}%`
                                                            : '-'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {report.classification_results.plan && (
                            <div style={{ marginBottom: '20px' }}>
                                <div className="detail-label-small">Analysis Plan</div>
                                <div className="detail-value-large" style={{ marginTop: '8px', lineHeight: '1.6' }}>
                                    {report.classification_results.plan}
                                </div>
                            </div>
                        )}

                        {report.classification_results.reasoning && report.classification_results.reasoning.length > 0 && (
                            <div>
                                <div className="detail-label-small">Reasoning</div>
                                <div style={{ marginTop: '12px' }}>
                                    {report.classification_results.reasoning.map((reason: string, index: number) => (
                                        <div key={index} style={{
                                            marginBottom: '12px',
                                            paddingLeft: '16px',
                                            borderLeft: '3px solid #e0e0e0',
                                            lineHeight: '1.6'
                                        }}>
                                            {reason}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Tags */}
                {report.tags && report.tags.length > 0 && (
                    <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '8px', border: '1px solid #e0e0e0', marginBottom: '20px', boxSizing: 'border-box' }}>
                        <h3 style={{ color: '#333', textAlign: 'left', marginBottom: '16px' }}>Tags</h3>
                        <div className="tags-container">
                            {report.tags.map((tag, index) => (
                                <span key={index} className="tag-badge">
                                    {tag}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {/* Financial Data */}
                <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '8px', border: '1px solid #e0e0e0', marginBottom: '20px', boxSizing: 'border-box' }}>
                    <h3 style={{ color: '#333', textAlign: 'left', marginBottom: '20px' }}>Financial Information</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(max(300px, 33.33%), 1fr))', maxWidth: '100%', gap: '16px', boxSizing: 'border-box' }}>
                        <div className="detail-item">
                            <div className="detail-label-small">ISIN</div>
                            <div className="detail-value-large">{report.isin || '-'}</div>
                        </div>
                        <div className="detail-item">
                            <div className="detail-label-small">CUSIP</div>
                            <div className="detail-value-large">{report.cusip || '-'}</div>
                        </div>
                        <div className="detail-item">
                            <div className="detail-label-small">Has ISIN</div>
                            <div className="detail-value-large">{report.has_isin ? 'Yes' : 'No'}</div>
                        </div>
                        <div className="detail-item">
                            <div className="detail-label-small">Has CUSIP</div>
                            <div className="detail-value-large">{report.has_cusip ? 'Yes' : 'No'}</div>
                        </div>
                        <div className="detail-item">
                            <div className="detail-label-small">Number of Bonds</div>
                            <div className="detail-value-large">{report.number_of_bonds || '-'}</div>
                        </div>
                        <div className="detail-item">
                            <div className="detail-label-small">Total Green Loan Portfolio</div>
                            <div className="detail-value-large">{report.totalGreenLoanPortfolio || '-'}</div>
                        </div>
                        <div className="detail-item">
                            <div className="detail-label-small">Total Impact</div>
                            <div className="detail-value-large">{report.totalImpact || '-'}</div>
                        </div>
                        <div className="detail-item">
                            <div className="detail-label-small">Total GHG Emissions Avoided</div>
                            <div className="detail-value-large">{report.total_GHG_emissions_avoided || '-'}</div>
                        </div>
                    </div>
                </div>

                {/* All Bonds Information */}
                {report.listOfAllBondISINs && report.listOfAllBondISINs.length > 0 && (
                    <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '8px', border: '1px solid #e0e0e0', marginBottom: '20px', boxSizing: 'border-box' }}>
                        <h3 style={{ color: '#333', textAlign: 'left', marginBottom: '16px' }}>All Bonds ({report.listOfAllBondISINs.length})</h3>
                        <div className="projects-list">
                            <table className="projects-table">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>ISIN</th>
                                        <th>Volume</th>
                                        <th>Currency</th>
                                        <th>Country</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {report.listOfAllBondISINs.map((isin: string, index: number) => (
                                        <tr key={index}>
                                            <td>{index + 1}</td>
                                            <td style={{ fontFamily: 'monospace' }}>{isin}</td>
                                            <td>{report.listOfAllBondVolumes?.[index] || '-'}</td>
                                            <td>{report.listOfAllBondCurrencies?.[index] || '-'}</td>
                                            <td>{report.allCountriesOfBonds?.[index] || '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Missing Bond ISINs */}
                {report.all_missing_bond_isins && report.all_missing_bond_isins.length > 0 && (
                    <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '8px', border: '1px solid #e0e0e0', marginBottom: '20px', boxSizing: 'border-box' }}>
                        <h3 style={{ color: '#333', textAlign: 'left', marginBottom: '16px' }}>Missing Bond ISINs ({report.all_missing_bond_isins.length})</h3>
                        <div className="projects-list">
                            <table className="projects-table">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>ISIN</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {report.all_missing_bond_isins.map((isin: string, index: number) => (
                                        <tr key={index}>
                                            <td>{index + 1}</td>
                                            <td style={{ fontFamily: 'monospace' }}>{isin}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Reasoning */}
                {report.reasoning && (
                    <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '8px', border: '1px solid #e0e0e0', marginBottom: '20px', boxSizing: 'border-box' }}>
                        <h3 style={{ color: '#333', textAlign: 'left', marginBottom: '16px' }}>Reasoning</h3>
                        <div style={{ backgroundColor: '#f9f9f9', padding: '16px', borderRadius: '4px', border: '1px solid #e0e0e0' }}>
                            <pre style={{ whiteSpace: 'pre-wrap', margin: 0, textAlign: 'left' }}>{report.reasoning}</pre>
                        </div>
                    </div>
                )}

                {/* Projects */}
                {report.projects && report.projects.length > 0 && (
                    <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '8px', border: '1px solid #e0e0e0', marginBottom: '20px', boxSizing: 'border-box' }}>
                        <h3 style={{ color: '#333', textAlign: 'left', marginBottom: '16px' }}>Projects ({report.projects.length})</h3>
                        <div className="projects-list">
                            <table className="projects-table">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Type</th>
                                        <th>Location</th>
                                        <th>Funds Allocation</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {report.projects.map((project: any, index: number) => (
                                        <tr key={index}>
                                            <td className="project-name">{project.name || '-'}</td>
                                            <td>{project.type || '-'}</td>
                                            <td>{project.location || '-'}</td>
                                            <td className="funds-allocation">
                                                {project.allocation_currency && project.funds_allocation
                                                    ? `${project.allocation_currency} ${(project.funds_allocation / 1000000).toFixed(0)}M`
                                                    : project.funds_allocation
                                                        ? `${(project.funds_allocation / 1000000).toFixed(0)}M`
                                                        : '-'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Technical Information */}
                <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '8px', border: '1px solid #e0e0e0', marginBottom: '20px', boxSizing: 'border-box' }}>
                    <h3 style={{ color: '#333', textAlign: 'left', marginBottom: '20px' }}>Technical Information</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(max(300px, 33.33%), 1fr))', maxWidth: '100%', gap: '16px', boxSizing: 'border-box' }}>
                        <div className="detail-item">
                            <div className="detail-label-small">Report Source Ref</div>
                            <div className="detail-value-large" style={{ fontFamily: 'monospace', fontSize: '14px' }}>{report.report_source_ref || '-'}</div>
                        </div>
                        <div className="detail-item">
                            <div className="detail-label-small">Search Result Ref</div>
                            <div className="detail-value-large" style={{ fontFamily: 'monospace', fontSize: '14px' }}>{report.search_result_ref || '-'}</div>
                        </div>
                        <div className="detail-item">
                            <div className="detail-label-small">Update Timestamp</div>
                            <div className="detail-value-large">{formatDate(report.update_timestamp)}</div>
                        </div>
                        <div className="detail-item">
                            <div className="detail-label-small">Validation Timestamp</div>
                            <div className="detail-value-large">{formatDate(report.validation_timestamp)}</div>
                        </div>
                        <div className="detail-item">
                            <div className="detail-label-small">Validation ISIN</div>
                            <div className="detail-value-large">{report.validation_isin || '-'}</div>
                        </div>
                        <div className="detail-item">
                            <div className="detail-label-small">Validation CUSIP</div>
                            <div className="detail-value-large">{report.validation_cusip || '-'}</div>
                        </div>
                        <div className="detail-item">
                            <div className="detail-label-small">Number of Missing Bonds</div>
                            <div className="detail-value-large">{report.number_of_missing_bonds || '-'}</div>
                        </div>
                        <div className="detail-item">
                            <div className="detail-label-small">Report References</div>
                            <div className="detail-value-large">{formatArray(report.report_ref)}</div>
                        </div>
                    </div>
                </div>

                {/* Raw Data Section */}
                {showRawData && (
                    <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '8px', border: '1px solid #e0e0e0', marginBottom: '20px', boxSizing: 'border-box' }}>
                        <h3 style={{ color: '#333', textAlign: 'left', marginBottom: '16px' }}>Raw Data</h3>
                        <div style={{ backgroundColor: '#f9f9f9', padding: '16px', borderRadius: '4px', border: '1px solid #e0e0e0' }}>
                            <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px', margin: 0, textAlign: 'left', fontFamily: 'monospace' }}>
                                {JSON.stringify(report, null, 2)}
                            </pre>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReportDetailView; 